import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Tag, ArrowRight, Search, Filter, FileText, Bell } from 'lucide-react';
import './UpdatesPage.css';

// Dummy data for updates
const updates = [
    {
        id: 1,
        title: "Ufficient AI Assistant 2.0 Released",
        date: "April 15, 2023",
        category: "Product Release",
        summary: "We're thrilled to announce the release of Ufficient AI Assistant 2.0, featuring improved response accuracy, faster processing, and expanded knowledge base capabilities.",
        content: "Our latest AI Assistant update brings significant improvements to how organizations interact with their knowledge base. With enhanced natural language understanding, the assistant can now better interpret complex queries and provide more accurate, contextual responses. The update also introduces advanced document summarization features and improved integration with third-party platforms.",
        image: "ChatGPT Image Apr 5, 2025, 09_40_15 AM.png",
        tags: ["AI Assistant", "Release", "Knowledge Management"]
    },
    {
        id: 2,
        title: "Document Management System Updates",
        date: "March 22, 2023",
        category: "Feature Update",
        summary: "The latest update to our Document Management System brings improved OCR capabilities, better metadata handling, and enhanced search functionality.",
        content: "Our Document Management System now features state-of-the-art OCR technology that can accurately extract text from scanned documents, handwritten notes, and complex diagrams. The improved metadata handling system makes it easier to categorize and find documents when needed. The enhanced search functionality now supports more complex queries and provides more relevant results.",
        image: "DocumentManagmentUpdates.png",
        tags: ["Document Management", "OCR", "Search"]
    },
    {
        id: 3,
        title: "New Integration Partnerships Announced",
        date: "February 10, 2023",
        category: "Partnership",
        summary: "Ufficient is excited to announce new integration partnerships with leading enterprise software providers, expanding our ecosystem of connected tools.",
        content: "We're expanding our integration capabilities through strategic partnerships with leading enterprise software providers. These new integrations will allow seamless data flow between Ufficient and your existing tools, creating a more unified workflow. New partners include major CRM platforms, project management tools, and enterprise communication systems.",
        image: "Partnerships.png",
        tags: ["Integrations", "Partnership", "Ecosystem"]
    },
    {
        id: 4,
        title: "RecruitAI Module Enhancement",
        date: "January 18, 2023",
        category: "Feature Update",
        summary: "Our RecruitAI module has been enhanced with improved candidate matching algorithms and a redesigned interview question generator.",
        content: "The latest update to our RecruitAI module introduces our most advanced candidate matching system yet. Using contextual understanding of job requirements and candidate qualifications, the system can now identify more suitable matches with higher accuracy. The redesigned interview question generator creates customized, role-specific questions that help assess candidates more effectively.",
        image: "RecruitAI.png",
        tags: ["RecruitAI", "HR", "Recruitment"]
    },
    {
        id: 5,
        title: "Security Enhancements Across Platform",
        date: "December 5, 2022",
        category: "Security",
        summary: "We've implemented comprehensive security enhancements across the Ufficient platform, including advanced encryption and improved access controls.",
        content: "Security remains our top priority, and our latest update includes significant enhancements to our security infrastructure. We've implemented advanced encryption protocols for data at rest and in transit, enhanced our access control systems with multi-factor authentication options, and added detailed security audit logs. These changes ensure your organization's data remains protected against evolving security threats.",
        image: "SecurityEnhancements.png",
        tags: ["Security", "Encryption", "Compliance"]
    },
    {
        id: 6,
        title: "BidAI Update: Contract Analysis Features",
        date: "November 14, 2022",
        category: "Feature Update",
        summary: "BidAI now includes advanced contract analysis features to help identify risks, opportunities, and compliance issues in procurement documents.",
        content: "The latest update to our BidAI module introduces sophisticated contract analysis capabilities. The system can now automatically identify potential risks, highlight opportunities for cost savings, and flag compliance issues in procurement documents. This feature helps procurement teams make more informed decisions and negotiate better terms with suppliers.",
        image: "BidAIUpdate.png",
        tags: ["BidAI", "Contract Analysis", "Procurement"]
    },
];

const categories = ["All", "Product Release", "Feature Update", "Partnership", "Security", "Webinar"];

const UpdatesPage = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [expandedUpdateId, setExpandedUpdateId] = useState(null);
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Filter updates based on search query and selected category
    const filteredUpdates = updates.filter(update => {
        const matchesSearch = 
            update.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            update.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
            update.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
            update.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
        
        const matchesCategory = selectedCategory === 'All' || update.category === selectedCategory;
        
        return matchesSearch && matchesCategory;
    });

    const toggleUpdateExpansion = (id) => {
        setExpandedUpdateId(expandedUpdateId === id ? null : id);
    };

    const handleSubscribe = (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        // In a real app, you would handle the subscription here
        
        // Reset form after 3 seconds
        setTimeout(() => {
            setIsSubmitted(false);
            setEmail('');
        }, 3000);
    };

    return (
        <div className="updates-page">
            {/* Hero Section */}
            <div className="updates-hero">
                <h1>Product Updates & News</h1>
                <p className="hero-description">
                    Stay informed about the latest improvements, features, and news from Ufficient.
                </p>

                {/* Subscribe section */}
                <div className="subscribe-section">
                    <form className="subscribe-form" onSubmit={handleSubscribe}>
                        <div className="input-wrapper">
                            <Bell className="input-icon" size={20} />
                            <input 
                                type="email" 
                                placeholder="Enter your email to receive updates" 
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <button type="submit" className="subscribe-button">
                            Subscribe
                            <ArrowRight size={20} />
                        </button>
                    </form>
                    {isSubmitted && (
                        <div className="submission-message">
                            Thank you! You're now subscribed to our updates.
                        </div>
                    )}
                </div>
            </div>

            {/* Filter and Search Section */}
            <div className="updates-filters">
                <div className="container">
                    <div className="search-container">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search updates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="category-filters">
                        <Filter size={20} className="filter-icon" />
                        <div className="category-buttons">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category)}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Updates List */}
            <div className="updates-content">
                <div className="container">
                    {filteredUpdates.length > 0 ? (
                        <div className="updates-list">
                            {filteredUpdates.map(update => (
                                <div
                                    key={update.id}
                                    className={`update-card ${expandedUpdateId === update.id ? 'expanded' : ''}`}
                                    onClick={() => toggleUpdateExpansion(update.id)}
                                >
                                    <div className="update-header">
                                        {update.image && (
                                            <div className="update-image-container">
                                                <img src={update.image} alt={update.title} className="update-image" />
                                            </div>
                                        )}
                                        <div className="update-meta">
                                            <span className="update-category">{update.category}</span>
                                            <div className="update-date">
                                                <Calendar size={16} />
                                                <span>{update.date}</span>
                                            </div>
                                        </div>
                                        <h2 className="update-title">{update.title}</h2>
                                        <p className="update-summary">{update.summary}</p>
                                    </div>
                                    
                                    {expandedUpdateId === update.id && (
                                        <div className="update-details">
                                            <p className="update-content">{update.content}</p>
                                            <div className="update-tags">
                                                <Tag size={16} />
                                                {update.tags.map((tag, index) => (
                                                    <span key={index} className="tag">{tag}</span>
                                                ))}
                                            </div>
                                            <a href="#" className="read-more-link">
                                                Read full article
                                                <ArrowRight size={16} />
                                            </a>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-results">
                            <FileText size={48} />
                            <h3>No updates found</h3>
                            <p>Try adjusting your search or filter criteria.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Load More / Pagination Section */}
            <div className="updates-pagination">
                <div className="container">
                    <button className="load-more-button">
                        Load more updates
                        <ArrowRight size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdatesPage; 